import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import './index.css'
import { isNative, initToken } from './lib/config'
import { DialogProvider } from './components/ui/dialogs'

// Native (Capacitor): StatusBar überlagert den WebView (edge-to-edge), damit die
// CSS-Safe-Areas (env(safe-area-inset-*)) greifen; dunkle Icons für den hellen Header.
if (isNative) {
  void (async () => {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar')
      await StatusBar.setOverlaysWebView({ overlay: true })
      await StatusBar.setStyle({ style: Style.Dark })
    } catch {
      /* Plugin nicht verfügbar – ignorieren */
    }
  })()
} else {
  // Service Worker nur im Web registrieren (ADR-068). registerType 'autoUpdate' →
  // frische App-Version beim nächsten Laden; Web-Push hängt an dieser Registrierung.
  void (async () => {
    try {
      const { registerSW } = await import('virtual:pwa-register')
      registerSW({ immediate: true })
    } catch {
      /* PWA im Dev evtl. deaktiviert – ignorieren */
    }
  })()
}

// App-Höhe = sichtbares Fenster (VisualViewport). Schrumpft die iOS-Software-Tastatur
// das sichtbare Fenster, verkleinert sich die App mit (das fixierte #root sitzt exakt
// darüber, siehe index.css) — das Eingabefeld bleibt sichtbar. Zusätzlich wird ein
// iOS-Fokus-Scroll des Fensters aktiv zurückgesetzt, damit die App nicht nach oben
// wandert (ADR-065/069).
const setAppHeight = (): void => {
  const h = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--app-height', `${Math.round(h)}px`)
  // iOS scrollt beim Fokussieren eines Eingabefelds trotz overflow:hidden das Fenster —
  // sofort zurückholen, damit #root (position:fixed, top:0) über der Tastatur bleibt.
  if (window.scrollY !== 0 || window.pageYOffset !== 0) window.scrollTo(0, 0)
}
setAppHeight()
window.visualViewport?.addEventListener('resize', setAppHeight)
window.visualViewport?.addEventListener('scroll', setAppHeight)
window.addEventListener('resize', setAppHeight)
window.addEventListener('orientationchange', setAppHeight)
// Die iOS-Tastatur meldet ihre Höhe teils erst einen Tick nach dem Fokus → nachmessen.
window.addEventListener('focusin', () => {
  setAppHeight()
  window.setTimeout(setAppHeight, 250)
})
window.addEventListener('focusout', () => {
  window.setTimeout(setAppHeight, 100)
})

async function bootstrap(): Promise<void> {
  // Nativ: Bearer-Token aus der Keychain/Keystore laden, BEVOR die App rendert
  // (getToken() ist synchron; ADR-068). Im Web sofort fertig (No-op).
  // Absicherung (ADR-072): niemals länger als 2 s blockieren und jeden Fehler
  // schlucken — sonst könnte ein Init-Problem den ganzen Render verhindern
  // (weißer Bildschirm). getToken() fällt intern auf localStorage zurück.
  try {
    await Promise.race([initToken(), new Promise((resolve) => window.setTimeout(resolve, 2000))])
  } catch {
    /* Token-Init fehlgeschlagen → App rendert trotzdem, getToken() nutzt Fallback */
  }

  const rootEl = document.getElementById('root')
  if (!rootEl) {
    throw new Error('#root nicht gefunden')
  }

  createRoot(rootEl).render(
    <StrictMode>
      <DialogProvider>
        <App />
      </DialogProvider>
    </StrictMode>,
  )
}

void bootstrap()
